import { createColumns } from "@/v2-backoffice-client/components/table"
import { DotText } from "@/v2-backoffice-client/components/dot"
import LicensePackageBadges from "@/v2-backoffice-client/license/changes/LicensePackageBadges"
import { mapContentSheetHref } from "@/v2-backoffice-client/license/changes/license.changes.constants"
import {
  PLACEMENT,
  placementOf,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"
import {
  contentDecisionOf,
  contentKindOf,
  contentPackagesOf,
  contentStatusOf,
  CONTENT_DECISION_DOT_VARIANT,
  isLockedContent,
  type LicenseContentItem,
} from "@/v2-backoffice-client/license/changes/license.content.utils"

// The Content view's columns: one map layer or base map per row, by the
// name the map shows, whether anyone can see it yet, what has been decided
// about it and which packages carry it. The same shape as the Capabilities
// view's, minus what content does not have (a change feed, a release, a
// "needs a decision"): content arrives when the review queue publishes it
// and is free until decided in its panel, which the row opens.
export function createContentColumns() {
  return createColumns<LicenseContentItem>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      Content: {
        id: "Content",
        label: "Content",
        description:
          "A base map or a map layer, by the name the map shows and its code. The two classes the map checks a user for; what a package can carry. Open it for its description and the decision.",
        enableHiding: false,
        defaultSize: 280,
        isWrapped: true,
        getValue: (row) => `${row.Name} ${row.Code}`,
        to: ({ row }) => mapContentSheetHref(row.original.Code),
        renderCell({ row }) {
          return (
            <span className="flex min-w-0 flex-col">
              <span className="font-medium">{row.original.Name}</span>
              <span className="font-mono text-[11px] font-normal text-muted-foreground">
                {row.original.Code}
              </span>
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Name
        },
      },
      Kind: {
        id: "Kind",
        label: "Kind",
        description: "Base map or map layer.",
        defaultSize: 100,
        enableGrouping: true,
        filter: { type: "text", placeholder: "Kind..." },
        getValue: (row) => contentKindOf(row),
      },
      Live: {
        id: "Live",
        label: "Live",
        description:
          "Whether the review queue has published it. Nobody sees an item that is not live, whatever is decided here; the decision waits for it.",
        defaultSize: 90,
        enableGrouping: true,
        getValue: (row) => (row.IsEnabled ? "Live" : "Not live"),
        renderCell({ row }) {
          return (
            <DotText variant={row.original.IsEnabled ? "success-1" : "muted"}>
              {row.original.IsEnabled ? "Live" : "Not live"}
            </DotText>
          )
        },
      },
      Countries: {
        id: "Countries",
        label: "Countries",
        description:
          "Where the map offers it. An item with no country is hidden everywhere, however it is decided; countries are set on its own sheet.",
        defaultSize: 110,
        getValue: (row) => row.CountryCodes ?? "",
        renderCell({ row }) {
          const countries = row.original.CountryCodes
          if (!countries) {
            return <span className="text-destructive">None: hidden</span>
          }
          return <span>{countries}</span>
        },
      },
      // The decision, with Not live set apart so the pills can single it
      // out: whatever is decided, nobody sees an unpublished item. Kept
      // under the id "Status" so the pills' URL state keeps working.
      Status: {
        id: "Status",
        label: "Decision",
        description:
          "Free for everyone: every customer sees it, how an item arrives. Package-only: locked to the packages that include it. Not live: unpublished, so nobody sees it yet whatever is decided.",
        defaultSize: 150,
        enableGrouping: true,
        getValue: (row) => contentStatusOf(row),
        renderCell({ row }) {
          const decision = contentDecisionOf(row.original)
          return (
            <DotText variant={CONTENT_DECISION_DOT_VARIANT[decision]}>
              {decision}
            </DotText>
          )
        },
      },
      Placement: {
        id: "Placement",
        label: "Packages",
        description:
          "The packages that carry the item, by a pick or by a rule (a whole class, a layer category), each opening its panel. For a package-only item they are who gets it. For a free one they are a placement: it changes nothing today and says which packages keep it if it is ever made package-only. Packages are composed on the Packages board; this board only decides who has the item.",
        defaultSize: 220,
        isWrapped: true,
        enableGrouping: true,
        getValue: (row) => placementOf(row),
        renderCell({ row }) {
          const item = row.original
          const isLocked = isLockedContent(item)
          return (
            <LicensePackageBadges
              names={contentPackagesOf(item)}
              variant={isLocked ? "neutral" : "outline"}
              emptyLabel={isLocked ? "Nobody yet" : PLACEMENT.unplaced}
              isEmptyCritical={isLocked}
              tooltip={
                isLocked
                  ? "Customers get it through this package. Open it to see what else it carries."
                  : "Already placed here: changes nothing while it is free, and keeps it for this package's customers the day it is made package-only."
              }
            />
          )
        },
        exportValue({ row }) {
          return (
            contentPackagesOf(row.original).join(", ") || PLACEMENT.unplaced
          )
        },
      },
    },
  })
}
